import React, { useCallback, useEffect } from 'react';
import { useI18n } from '../../../context/I18nContext';
import { useSidebarToggle } from './useSidebarToggle';
import './sidebar-controls.css';

/**
 * Sol panel kontrolleri: ac/kapa butonu ve kenardan surukleyerek genislik ayari.
 * Genislik <html> uzerindeki CSS degiskeniyle uygulanir; #main-wrapper sinifini
 * React yonettigi icin oraya dokunulmaz. Secilen genislik tarayicida hatirlanir.
 */

const STORAGE_KEY = 'wc.sidebarWidth';
const MIN_WIDTH = 240;
const MAX_WIDTH = 420;
const KEYBOARD_STEP = 16;

const clampWidth = (value: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));

const applyWidth = (width: number | null) => {
    const root = document.documentElement;
    if (width === null) {
        delete root.dataset.wcSidebar;
        root.style.removeProperty('--wc-sidebar-width');
        return;
    }
    root.dataset.wcSidebar = 'custom';
    root.style.setProperty('--wc-sidebar-width', `${width}px`);
};

const readStoredWidth = (): number | null => {
    try {
        const stored = Number(window.localStorage.getItem(STORAGE_KEY));
        return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : null;
    } catch {
        return null;
    }
};

const storeWidth = (width: number | null) => {
    try {
        if (width === null) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
        // Depolama kapaliysa genislik yalnizca bu oturumda gecerli kalir.
    }
};

const currentSidebarWidth = (handle: HTMLElement) =>
    handle.closest('.deznav')?.getBoundingClientRect().width ?? MIN_WIDTH;

/** Paneli dar (yalnizca ikon) ve genis mod arasinda degistiren buton. */
export const SidebarToggle = () => {
    const { t } = useI18n();
    const { isCollapsed, toggle } = useSidebarToggle();
    const label = t(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse');

    return (
        <div className="wc-sidebar-toggle-row">
            <button
                type="button"
                className="wc-sidebar-toggle"
                onClick={toggle}
                aria-label={label}
                aria-expanded={!isCollapsed}
                title={label}
            >
                <i className={`fa-solid ${isCollapsed ? 'fa-angles-right' : 'fa-angles-left'}`} aria-hidden="true" />
            </button>
        </div>
    );
};

/** Panelin sag kenari: surukleyerek, ok tuslariyla ya da cift tiklayarak (sifirlama) genislik ayari. */
export const SidebarResizer = () => {
    const { t } = useI18n();
    const { isCollapsed } = useSidebarToggle();

    useEffect(() => {
        applyWidth(readStoredWidth());
    }, []);

    const commitWidth = useCallback((width: number) => {
        const next = clampWidth(width);
        applyWidth(next);
        storeWidth(next);
    }, []);

    const resetWidth = useCallback(() => {
        applyWidth(null);
        storeWidth(null);
    }, []);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const sidebarLeft = event.currentTarget.closest('.deznav')?.getBoundingClientRect().left ?? 0;
        const root = document.documentElement;
        root.classList.add('wc-sidebar-resizing');

        const handleMove = (moveEvent: PointerEvent) => {
            applyWidth(clampWidth(moveEvent.clientX - sidebarLeft));
        };
        const handleUp = (upEvent: PointerEvent) => {
            root.classList.remove('wc-sidebar-resizing');
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            commitWidth(upEvent.clientX - sidebarLeft);
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? KEYBOARD_STEP : -KEYBOARD_STEP;
        commitWidth(currentSidebarWidth(event.currentTarget) + delta);
    };

    if (isCollapsed) return null;

    return (
        <div
            className="wc-sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label={t('sidebar.resize')}
            title={t('sidebar.resize')}
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onDoubleClick={resetWidth}
            onKeyDown={handleKeyDown}
        />
    );
};
